<script setup lang="ts">
  import { LIcon, LLayerGroup, LMarker, LTooltip } from '@vue-leaflet/vue-leaflet'
  import { PropType } from 'vue'
  import { GITypeDisplay, type GroundInvestigationPoint } from '@/stores/type/giPoint.type'
  import { useGIDataUploadCollectionStore } from '@/stores/giDataUploadCollection'
  import { useEventsBus } from '@haskoning/vue-component-library'
  import { getGIDipslayName } from '@/utils/giPoint'
  import { result } from 'lodash'

  const props = defineProps({
    item: {
      type: Object as PropType<GroundInvestigationPoint>,
      required: true
    },
    map: {
      type: Object,
      required: true
    }
  })

  const store = useProjectStore()

  const { emit: busEmit } = useEventsBus()

  const snackbarStore = useSnackbarStore()
  const giDataUploadCollectionsStore = useGIDataUploadCollectionStore()
  const giPointsStore = useGIPointsStore()
  const sectionLinesStore = useSectionLinesStore()
  const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
  const marker = ref()
  const markerText = ref()
  const emit = defineEmits(['fly-to-marker'])
  const { visualiseItem: visualiseSectionLine, selectedItems: selectedSectionLines } =
    storeToRefs(sectionLinesStore)
  const { selectedItems: selectedGIPoints } = storeToRefs(giPointsStore)
  const { giDataCollectionByID } = storeToRefs(giDataUploadCollectionsStore)
  const { profileDrawer } = storeToRefs(store)

  const markerTextColor = computed(() => {
    const giDataCollection = giDataCollectionByID.value(
      props.item.properties.gi_data_upload_collection
    )
    if (giDataCollection) {
      return giDataCollection.collection_color || '#000000'
    }
  })

  const markerColor = computed(() => {
    if (visualiseSectionLine.value) {
      const selection = sectionLinesGIPointsStore.giPointsSelection(
        props.item.id,
        visualiseSectionLine.value.id
      )
      if (selection) {
        return visualiseSectionLine.value.properties.section_color
      }
    }
    return 'black'
  })

  const markerIcon = computed(() => {
    const resultMapping = {
      [GITypeDisplay.BOREHOLE]: 'mdi-circle',
      [GITypeDisplay.CPT]: 'mdi-triangle-down',
      [GITypeDisplay.UNKNOWN]: 'mdi-help-circle'
    }
    return resultMapping[getGIDipslayName(props.item)]
  })

  const isSelected = computed(() => {
    return selectedGIPoints.value.map((point) => point.id).includes(props.item.id)
      ? 'selected'
      : 'selection-zone'
  })
  const reversedCoordinates = computed(() => {
    const coordinates = [...(props.item.geometry.coordinates as [number, number])]
    return [coordinates[1], coordinates[0]]
  })

  const addGIPointSelections = async () => {
    console.log(selectedSectionLines.value)
    if (selectedSectionLines.value.length === 0) {
      busEmit('error', {
        status: 400,
        statusText: 'Please select a section line',
        data: '',
        method: 'POST'
      })
      return
    } else {
      if (!selectedGIPoints.value.length) {
        giPointsStore.addSelectedGIPoint(props.item)
      }
      const giPointIDs = selectedGIPoints.value.map((item: GroundInvestigationPoint) => item.id)
      for (const sectionLine of selectedSectionLines.value) {
        await sectionLinesGIPointsStore.addGIPointsToSectionLine(giPointIDs, sectionLine.id)
      }
    }
  }

  const removeGIPointSelections = async () => {
    if (selectedSectionLines.value.length === 0) {
      snackbarStore.setMessage('Please select a section line', 'error', 400)
      return
    } else {
      if (!selectedGIPoints.value.length) {
        giPointsStore.addSelectedGIPoint(props.item)
      }
      const giPointIDs = selectedGIPoints.value.map((item: GroundInvestigationPoint) => item.id)
      for (const sectionLine of selectedSectionLines.value) {
        await sectionLinesGIPointsStore.removeGIPointsFromSectionLine(giPointIDs, sectionLine.id)
      }
    }
  }

  const onMarkersReady = () => {
    marker.value.leafletObject.on('click', () => {
      giPointsStore.addSelectedGIPoint(props.item)
    })

    props.map.leafletObject.getZoom() < 16
      ? props.map.leafletObject.removeLayer(markerText.value.leafletObject)
      : props.map.leafletObject.addLayer(markerText.value.leafletObject)

    props.map.leafletObject.on('zoomend', () => {
      if (markerText.value) {
        if (props.map.leafletObject.getZoom() < 16) {
          props.map.leafletObject.removeLayer(markerText.value.leafletObject)
        } else {
          props.map.leafletObject.addLayer(markerText.value.leafletObject)
        }
      }
    })
  }

  const markerOptions = {
    contextmenu: store.profileDrawer.shown,
    contextmenuInheritItems: false,
    contextmenuItems: [
      {
        text: 'Select GI Points',
        callback: addGIPointSelections,
        index: 0
      },
      {
        text: 'Deselect GI Points',
        callback: removeGIPointSelections,
        index: 1
      }
    ]
  }

  watch(
    () => giPointsStore.currentItem,
    () => {
      if (props.item.id === giPointsStore.currentItem!.id) {
        emit('fly-to-marker', props.item.geometry.coordinates)
      }
    }
  )

  // This is a workaround to make sure that the context menu can be updated with certain variables
  watch(profileDrawer.value, (options) => {
    const markerLeafletObject = marker.value.leafletObject
    if (!markerLeafletObject) {
      return
    }
    if (options.shown) {
      markerLeafletObject.bindContextMenu(options)
    } else {
      markerLeafletObject.unbindContextMenu()
    }
  })
</script>

<template>
  <l-layer-group>
    <l-marker
      ref="marker"
      :options="markerOptions"
      :lat-lng="reversedCoordinates"
      :key="item.id"
      @ready="onMarkersReady"
    >
      <l-icon class-name="" :icon-size="[20, 20]">
        <v-icon size="medium" :color="markerColor" :class="isSelected">
          {{ markerIcon }}
        </v-icon>
      </l-icon>
      <l-tooltip>
        <div>
          <div>Total Length: {{ props.item.properties.end_depth }}</div>
          <div>Survey Company: {{ props.item.properties.survey_company }}</div>
          <div>Survey Date: {{ props.item.properties.survey_datestring }}</div>
        </div>
      </l-tooltip>
    </l-marker>
    <l-marker :lat-lng="reversedCoordinates" ref="markerText">
      <l-icon class-name="">
        <div class="text-below-marker" :style="{ color: markerTextColor }">
          {{ props.item.properties.location_id }}
        </div>
      </l-icon>
    </l-marker>
  </l-layer-group>
</template>

<style scoped>
  .text-below-marker {
    font-weight: bolder;
    position: absolute;
    margin-top: 15px;
    left: -45px;
    min-width: 100px;
    min-height: 20px;
    text-align: center;
    pointer-events: none;
  }

  .selected,
  .selection-zone {
    left: -4px;
    width: 120%;
    height: 120%;
    position: absolute;
    border-radius: 100%;
    opacity: 0.7;
    background-color: red;
    pointer-events: all;
  }

  .selected::after,
  .selection-zone::after {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    background-color: transparent;
    cursor: pointer;
    pointer-events: all;
  }

  .selection-zone {
    background-color: transparent;
  }
</style>
